import { useEffect, useRef } from "react";

import Confetti from "./Confetti.jsx";

/**
 * PRCelebration — a quiet, earned moment.
 *
 * Deliberately not a scoreboard. The person's own sentence leads; the load and
 * reps are a supporting line underneath, at reading size. No projection, no
 * "next target", no big numeral — a future number on screen functions as a
 * target however it is captioned, and this product does not set targets.
 *
 * A soft disc beside the label, a sentence, and a way out. There was a ring
 * that closed on mount here; it was the activity-ring motif that
 * StreakMilestone rejects by name — a circle you can complete is a circle you
 * can fail to complete — and at rest, which is where it spends nearly all of
 * its life, it read as an unchecked radio button. The moment is earned by the
 * sentence and by the card settling in, not by a completion arc.
 */

export default function PRCelebration({ pr, onDone, onShare }) {
  const dialogRef = useRef(null);
  const overlayRef = useRef(null);

  // Focus moves in, everything behind leaves the tab order and the a11y tree,
  // and focus goes back where it came from on close.
  useEffect(() => {
    const node = dialogRef.current;
    const previous = document.activeElement;
    node?.focus({ preventScroll: true });

    const locked = [];
    for (let el = overlayRef.current; el && el !== document.body; el = el.parentElement) {
      for (const sibling of el.parentElement?.children || []) {
        if (sibling === el || sibling.inert) continue;
        if (sibling.matches?.('[role="dialog"]') || sibling.querySelector?.('[role="dialog"]')) {
          continue;
        }
        sibling.inert = true;
        locked.push(sibling);
      }
    }

    return () => {
      for (const el of locked) el.inert = false;
      if (previous instanceof HTMLElement) previous.focus({ preventScroll: true });
    };
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onDone?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onDone]);

  return (
    <div
      ref={overlayRef}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 500,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "var(--gutter)",
      }}
    >
      {/* Tapping outside dismisses — as a real control, not a div with onClick. */}
      <button
        type="button"
        aria-label="Dismiss"
        onClick={() => onDone?.()}
        className="ds-fade-in"
        style={{
          position: "absolute",
          inset: 0,
          width: "100%",
          minHeight: 0,
          padding: 0,
          border: 0,
          borderRadius: 0,
          background: "var(--scrim)",
          cursor: "pointer",
        }}
      />

      {/* The moment, and only for a moment: a warm bloom of light rises behind
          the card and is gone inside two seconds. It sits between the scrim and
          the dialog so it lights the card from behind rather than washing over
          the words, takes no pointer events, and is invisible to assistive tech
          — everything it says is already said in the sentence below. */}
      <Confetti active zIndex={0} />

      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="pr-celebration-title"
        tabIndex={-1}
        ref={dialogRef}
        className="ds-settle-in"
        style={{
          position: "relative",
          width: "100%",
          maxWidth: "320px",
          background: "var(--surface)",
          border: "1px solid var(--border)",
          borderRadius: "var(--radius-xl)",
          boxShadow: "var(--shadow-lg)",
          padding: "var(--card-pad-lg)",
          /* The container takes focus on mount so a screen reader lands inside
             the dialog. It is not itself a control, so it doesn't wear a ring —
             every real control in here still does. */
          outline: "none",
        }}
      >
        {/* The mark sits on the eyebrow's line rather than alone above it, so
            the left column reads as one column instead of an L-shaped void.
            No tick — a checkmark is form-submitted iconography — and no ring:
            a soft filled disc holding the exercise's initial, with nothing to
            complete and nothing to lose. Same solution StreakMilestone uses. */}
        <div
          className="ds-row"
          style={{ gap: "var(--space-3)", marginBottom: "var(--stack)" }}
        >
          <span
            aria-hidden="true"
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: "32px",
              height: "32px",
              flexShrink: 0,
              borderRadius: "var(--radius-circle)",
              background: "var(--accent-soft)",
              fontSize: "var(--text-xs)",
              fontWeight: "var(--weight-medium)",
              lineHeight: 1,
              color: "var(--text2)",
            }}
          >
            {(pr.exerciseName || "").trim().charAt(0).toUpperCase()}
          </span>
          <p className="section-label" style={{ marginBottom: 0 }}>
            Your best so far
          </p>
        </div>

        <h2
          id="pr-celebration-title"
          className="ds-lead"
          style={{ marginBottom: "var(--space-2)", textWrap: "pretty" }}
        >
          That's the most you've lifted on {pr.exerciseName}.
        </h2>

        <p className="ds-support" style={{ textWrap: "pretty" }}>
          {pr.weight} lbs for {pr.reps} reps — more than you'd done before.
        </p>

        <div
          className="ds-row"
          style={{ marginTop: "var(--stack-lg)", gap: "var(--space-2)" }}
        >
          <button
            type="button"
            className="btn btn-primary"
            style={{ flex: 1 }}
            onClick={() => onDone?.()}
          >
            Continue
          </button>
          {onShare && (
            <button
              type="button"
              className="btn btn-quiet"
              style={{ minWidth: "var(--tap)" }}
              aria-label="Share this"
              onClick={() => onShare?.()}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <circle cx="18" cy="5" r="3" />
                <circle cx="6" cy="12" r="3" />
                <circle cx="18" cy="19" r="3" />
                <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
                <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
              </svg>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
